import { useEffect, useId, useMemo, useRef, useState } from 'react';

/**
 * Ro'yxatdan tanlash.
 *
 * Brauzerning `<select>` i har bir OS'da butunlay boshqacha ko'rinadi:
 * macOS'da to'q kulrang tizim menyusi, Windows'da oq, telefonda pastdan
 * chiqadigan g'ildirak. Sotuvchi bitta ekranda ikki xil uslub ko'rmasligi
 * kerak, shuning uchun ro'yxat o'zimizniki.
 *
 * Klaviatura standart `<select>` dagidek ishlaydi:
 *   ↑ ↓        — variantlar bo'ylab yurish
 *   Enter/bo'sh — ochish va tanlash
 *   Esc        — yopish
 *   Home/End   — birinchi/oxirgi variant
 *   harflar    — shu harf bilan boshlanadigan variantga o'tish
 *
 * Ekran o'quvchilar uchun ARIA rollari qo'yilgan (`combobox` + `listbox`),
 * shuning uchun ko'rinishi o'zgargani bilan foydalanish qulayligi
 * yo'qolmaydi.
 */

export interface SelectOption<T extends string> {
  value: T;
  label: string;
  /** Variantlar guruhi (masalan «Yaqin qarindosh»). */
  group?: string;
}

interface SelectProps<T extends string> {
  value: T;
  options: ReadonlyArray<SelectOption<T>>;
  onChange: (value: T) => void;
  /** Ekran o'quvchi uchun nom — ko'rinadigan yorliq bo'lmasa. */
  ariaLabel?: string;
  id?: string;
  disabled?: boolean;
  invalid?: boolean;
  /** Qo'shimcha sinf — kenglikni chaqiruvchi belgilaydi. */
  className?: string;
}

export function Select<T extends string>({
  value,
  options,
  onChange,
  ariaLabel,
  id,
  disabled,
  invalid,
  className = '',
}: SelectProps<T>) {
  const listId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const typed = useRef({ text: '', at: 0 });

  const [open, setOpen] = useState(false);
  const selectedIndex = Math.max(
    0,
    options.findIndex((o) => o.value === value),
  );
  const [activeIndex, setActiveIndex] = useState(selectedIndex);

  const current = options[selectedIndex];

  // Guruhlar bo'yicha bo'lib chiqamiz, tartibni saqlagan holda.
  const groups = useMemo(() => {
    const out: Array<{ name: string | undefined; items: Array<{ option: SelectOption<T>; index: number }> }> = [];
    options.forEach((option, index) => {
      const last = out[out.length - 1];
      if (last && last.name === option.group) last.items.push({ option, index });
      else out.push({ name: option.group, items: [{ option, index }] });
    });
    return out;
  }, [options]);

  // Tashqariga bosilsa yopiladi.
  useEffect(() => {
    if (!open) return;

    function onPointerDown(event: MouseEvent) {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  // Ochilganda tanlangan variant ko'rinib tursin.
  useEffect(() => {
    if (!open) return;
    setActiveIndex(selectedIndex);
    listRef.current
      ?.querySelector<HTMLElement>(`[data-index="${selectedIndex}"]`)
      ?.scrollIntoView({ block: 'nearest' });
  }, [open, selectedIndex]);

  function move(to: number) {
    const next = Math.min(options.length - 1, Math.max(0, to));
    setActiveIndex(next);
    listRef.current
      ?.querySelector<HTMLElement>(`[data-index="${next}"]`)
      ?.scrollIntoView({ block: 'nearest' });
  }

  function pick(index: number) {
    const option = options[index];
    if (option) onChange(option.value);
    setOpen(false);
  }

  /** Harf bosilganda — o'sha harfdan boshlanadigan variantga o'tish. */
  function jumpToLetter(letter: string) {
    const now = Date.now();
    typed.current.text = now - typed.current.at > 800 ? letter : typed.current.text + letter;
    typed.current.at = now;

    const query = typed.current.text.toLowerCase();
    const from = typed.current.text.length === 1 ? activeIndex + 1 : activeIndex;
    const order = [...options.slice(from), ...options.slice(0, from)];
    const found = order.find((o) => o.label.toLowerCase().startsWith(query));
    if (!found) return;

    const index = options.indexOf(found);
    if (open) move(index);
    else onChange(found.value);
  }

  function onKeyDown(event: React.KeyboardEvent) {
    if (disabled) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (open) move(activeIndex + 1);
        else setOpen(true);
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (open) move(activeIndex - 1);
        else setOpen(true);
        break;
      case 'Home':
        if (open) {
          event.preventDefault();
          move(0);
        }
        break;
      case 'End':
        if (open) {
          event.preventDefault();
          move(options.length - 1);
        }
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        if (open) pick(activeIndex);
        else setOpen(true);
        break;
      case 'Escape':
        if (open) {
          event.preventDefault();
          setOpen(false);
        }
        break;
      case 'Tab':
        setOpen(false);
        break;
      default:
        if (event.key.length === 1 && !event.metaKey && !event.ctrlKey) {
          jumpToLetter(event.key);
        }
    }
  }

  return (
    <div className={`select ${open ? 'is-open' : ''} ${className}`} ref={rootRef}>
      <button
        type="button"
        id={id}
        className="select__button field__input"
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-haspopup="listbox"
        {...(ariaLabel ? { 'aria-label': ariaLabel } : {})}
        aria-invalid={invalid ? true : undefined}
        disabled={disabled}
        onClick={() => setOpen((on) => !on)}
        onKeyDown={onKeyDown}
      >
        <span className="select__value">{current?.label ?? ''}</span>
        <span className="select__caret" aria-hidden="true" />
      </button>

      {open && (
        <div className="select__list" id={listId} role="listbox" ref={listRef} tabIndex={-1}>
          {groups.map((group) => (
            <div className="select__group" key={group.name ?? 'default'}>
              {group.name && <div className="select__group-label">{group.name}</div>}
              {group.items.map(({ option, index }) => (
                <div
                  key={option.value}
                  data-index={index}
                  role="option"
                  aria-selected={option.value === value}
                  className={`select__option ${index === activeIndex ? 'is-active' : ''} ${
                    option.value === value ? 'is-selected' : ''
                  }`}
                  // `onMouseDown` — `onClick` dan oldin ishlaydi va fokus
                  // yo'qolishidan oldin tanlashga ulguradi.
                  onMouseDown={(event) => {
                    event.preventDefault();
                    pick(index);
                  }}
                  onMouseEnter={() => setActiveIndex(index)}
                >
                  <span className="select__check" aria-hidden="true">
                    {option.value === value ? '✓' : ''}
                  </span>
                  {option.label}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
